import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Zap, Clock, Activity, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { Badge } from './ui/badge';

const PerformanceMetrics = ({ showByDefault = false }) => {
  const [metrics, setMetrics] = useState(null);
  const [isExpanded, setIsExpanded] = useState(showByDefault);
  const [isVisible, setIsVisible] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const location = useLocation();

  // Measure performance function
  const measurePerformance = useCallback(() => {
    if (typeof window !== 'undefined' && window.performance) {
      setIsMeasuring(true);
      
      // Small delay to ensure navigation is complete
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0];
        const paint = performance.getEntriesByType('paint');
        
        const fcp = paint.find(p => p.name === 'first-contentful-paint');
        const lcp = performance.getEntriesByType('largest-contentful-paint');
        
        // For SPA navigation, use performance.now() as a proxy for render time
        const now = performance.now();
        
        const metricsData = {
          // Navigation timing (initial page load)
          pageLoadTime: navigation ? Math.round(navigation.loadEventEnd - navigation.startTime) : Math.round(now),
          domContentLoaded: navigation ? Math.round(navigation.domContentLoadedEventEnd - navigation.startTime) : null,
          timeToFirstByte: navigation ? Math.round(navigation.responseStart - navigation.requestStart) : null,
          
          // Paint timing
          firstContentfulPaint: fcp ? Math.round(fcp.startTime) : null,
          largestContentfulPaint: lcp.length > 0 ? Math.round(lcp[lcp.length - 1].startTime) : null,
          
          // Resource count
          resourceCount: performance.getEntriesByType('resource').length,
          
          // Memory (Chrome only)
          memoryUsed: performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) : null,
          
          // Current route
          currentRoute: location.pathname,
          
          // Timestamp
          measuredAt: new Date().toLocaleTimeString(),
        };
        
        setMetrics(metricsData);
        setIsMeasuring(false);
      }, 150);
    }
  }, [location.pathname]);

  // Initial setup and visibility check
  useEffect(() => {
    const showMetrics = localStorage.getItem('viralla:showPerformanceMetrics') === 'true' || showByDefault;
    setIsVisible(showMetrics);
  }, [showByDefault]);

  // Re-measure on route change
  useEffect(() => {
    // Wait for page to fully load on initial load
    if (document.readyState === 'complete') {
      measurePerformance();
    } else {
      window.addEventListener('load', measurePerformance);
      return () => window.removeEventListener('load', measurePerformance);
    }
  }, [location.pathname, measurePerformance]);

  // Observe LCP updates
  useEffect(() => {
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          if (entries.length > 0) {
            setMetrics(prev => prev ? ({
              ...prev,
              largestContentfulPaint: Math.round(entries[entries.length - 1].startTime)
            }) : null);
          }
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
        
        return () => lcpObserver.disconnect();
      } catch (e) {
        // LCP observer not supported
      }
    }
  }, []);

  const toggleVisibility = () => {
    const newState = !isVisible;
    setIsVisible(newState);
    localStorage.setItem('viralla:showPerformanceMetrics', newState.toString());
  };

  const getPerformanceRating = (value, thresholds) => {
    if (value === null) return 'gray';
    if (value <= thresholds.good) return 'green';
    if (value <= thresholds.moderate) return 'yellow';
    return 'red';
  };

  const formatMs = (ms) => {
    if (ms === null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  if (!isVisible) {
    return (
      <button
        onClick={toggleVisibility}
        className="fixed bottom-4 right-4 z-50 p-2 bg-gray-800/80 hover:bg-gray-700/90 text-gray-400 hover:text-white rounded-full shadow-lg backdrop-blur-sm transition-all duration-200"
        title="Show Performance Metrics"
      >
        <Activity className="w-4 h-4" />
      </button>
    );
  }

  const ratingColors = {
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700/50 text-sm font-mono overflow-hidden max-w-xs">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 bg-gray-800/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Zap className={`w-4 h-4 text-purple-400 ${isMeasuring ? 'animate-pulse' : ''}`} />
          <span className="text-gray-200 font-medium text-xs">Performance</span>
          {isMeasuring && <span className="text-[10px] text-gray-500">measuring...</span>}
        </div>
        <div className="flex items-center gap-2">
          {!isMeasuring && metrics?.pageLoadTime && (
            <Badge 
              variant="outline" 
              className={`text-[10px] px-1.5 py-0 ${ratingColors[getPerformanceRating(metrics.pageLoadTime, { good: 1500, moderate: 3000 })]}`}
            >
              {formatMs(metrics.pageLoadTime)}
            </Badge>
          )}
          <button 
            onClick={(e) => { e.stopPropagation(); measurePerformance(); }}
            className="text-gray-500 hover:text-gray-300 p-0.5"
            title="Refresh metrics"
          >
            <RefreshCw className={`w-3 h-3 ${isMeasuring ? 'animate-spin' : ''}`} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); toggleVisibility(); }}
            className="text-gray-500 hover:text-gray-300"
          >
            ×
          </button>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </div>

      {/* Metrics Panel */}
      {isExpanded && metrics && (
        <div className="px-3 py-2 space-y-2">
          {/* Core Web Vitals */}
          <div className="space-y-1.5">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Core Metrics</div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">Page Load</span>
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${ratingColors[getPerformanceRating(metrics.pageLoadTime, { good: 1500, moderate: 3000 })]}`}
              >
                {formatMs(metrics.pageLoadTime)}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">First Contentful Paint</span>
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${ratingColors[getPerformanceRating(metrics.firstContentfulPaint, { good: 1800, moderate: 3000 })]}`}
              >
                {formatMs(metrics.firstContentfulPaint)}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">Largest Contentful Paint</span>
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${ratingColors[getPerformanceRating(metrics.largestContentfulPaint, { good: 2500, moderate: 4000 })]}`}
              >
                {formatMs(metrics.largestContentfulPaint)}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">DOM Content Loaded</span>
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${ratingColors[getPerformanceRating(metrics.domContentLoaded, { good: 1000, moderate: 2000 })]}`}
              >
                {formatMs(metrics.domContentLoaded)}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">Time to First Byte</span>
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${ratingColors[getPerformanceRating(metrics.timeToFirstByte, { good: 200, moderate: 500 })]}`}
              >
                {formatMs(metrics.timeToFirstByte)}
              </Badge>
            </div>
          </div>

          {/* Resource Info */}
          <div className="pt-2 border-t border-gray-700/50 space-y-1.5">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Resources</div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">Resources Loaded</span>
              <span className="text-gray-300 text-xs">{metrics.resourceCount}</span>
            </div>

            {metrics.memoryUsed && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-xs">Memory Used</span>
                <span className="text-gray-300 text-xs">{metrics.memoryUsed} MB</span>
              </div>
            )}

            {metrics.currentRoute && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-xs">Current Route</span>
                <span className="text-gray-300 text-xs font-normal truncate max-w-[120px]" title={metrics.currentRoute}>
                  {metrics.currentRoute}
                </span>
              </div>
            )}

            {metrics.measuredAt && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-xs">Measured At</span>
                <span className="text-gray-300 text-xs">{metrics.measuredAt}</span>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="pt-2 border-t border-gray-700/50">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-gray-500">Good</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span className="text-gray-500">Moderate</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span className="text-gray-500">Slow</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMetrics;
